import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { sessionQuery } from './api/session';
import { createAppRouter } from './router';
import './styles.css';
import { isApiError } from '@avrahami/ui';

/**
 * Any call that finds the session gone (expired, revoked, waiting for its second factor, or
 * without one where it is now required) makes the app re-read the session, and the layout then
 * sends the user to the right screen.
 */
function onApiError(error: unknown): void {
  if (
    isApiError(error, 'UNAUTHENTICATED') ||
    isApiError(error, 'SESSION_EXPIRED') ||
    isApiError(error, 'MFA_REQUIRED') ||
    isApiError(error, 'MFA_ENROLLMENT_REQUIRED')
  ) {
    void queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
  }
}

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.queryKey[0] !== sessionQuery.queryKey[0]) onApiError(error);
    },
  }),
  mutationCache: new MutationCache({ onError: onApiError }),
  defaultOptions: {
    queries: {
      retry: (failures, error) => !isApiError(error) && failures < 2,
      refetchOnWindowFocus: true,
      // A list someone returns to is still in memory, so it shows at once, at the same scroll
      // position, and refreshes behind the scenes.
      gcTime: 30 * 60 * 1000,
    },
  },
});

const router = createAppRouter(queryClient);

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');
createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
